import { type AssetManifest, AssetManifestSchema } from '@shefira/shared';
import type Phaser from 'phaser';

export const MANIFEST_CACHE_KEY = 'asset-manifest';
export const ASSETS_BASE_URL = 'assets/';

export function parseManifest(raw: unknown): AssetManifest {
  const result = AssetManifestSchema.safeParse(raw);
  if (!result.success) {
    throw new Error(`Invalid asset manifest: ${result.error.message}`);
  }
  return result.data;
}

let manifestOnce: Promise<AssetManifest> | undefined;

/**
 * The manifest, fetched and validated once per page: the title music, the
 * roster's dolls, the boot and every panel that draws a body all ask for it,
 * and a phone pays each validation of its thousands of keys. A failure is
 * not remembered, so the next caller tries again.
 */
export function fetchManifest(): Promise<AssetManifest> {
  manifestOnce ??= fetch(`${ASSETS_BASE_URL}manifest.json`)
    .then((response) => {
      if (!response.ok) throw new Error(`manifest.json: HTTP ${String(response.status)}`);
      return response.json() as Promise<unknown>;
    })
    .then(parseManifest)
    .catch((error: unknown) => {
      manifestOnce = undefined;
      throw error;
    });
  return manifestOnce;
}

/**
 * The atlases of the playable bodies that are not the player's own
 * (`characters-mage` while playing a Warrior): only someone else's character
 * can need them, so they load the first time one shows up (`loadLazyAtlases`)
 * rather than holding up the way in.
 */
export function otherBodyAtlases(manifest: AssetManifest, ownBodyKey: string): Set<string> {
  const own = manifest.sheets[ownBodyKey]?.atlas;
  const bodyAtlases = new Set(
    Object.entries(manifest.sheets)
      .filter(([key, sheet]) => key.startsWith('body.') && sheet.atlas.startsWith('characters'))
      .map(([, sheet]) => sheet.atlas),
  );
  // An atlas that also holds anything but bodies stays eager.
  for (const [key, sheet] of Object.entries(manifest.sheets)) {
    if (!key.startsWith('body.')) bodyAtlases.delete(sheet.atlas);
  }
  for (const sprite of Object.values(manifest.sprites)) bodyAtlases.delete(sprite.atlas);
  if (own !== undefined) bodyAtlases.delete(own);
  return bodyAtlases;
}

/**
 * Queue every atlas (but the lazy ones and those in `defer`) and every
 * tileset grid declared in the manifest on the scene loader.
 */
export function queueManifestAtlases(
  loader: Phaser.Loader.LoaderPlugin,
  manifest: AssetManifest,
  defer: ReadonlySet<string> = new Set(),
): void {
  for (const [key, atlas] of Object.entries(manifest.atlases)) {
    if (atlas.lazy === true || defer.has(key)) continue;
    loader.atlas(key, ASSETS_BASE_URL + atlas.image, ASSETS_BASE_URL + atlas.data);
  }
  // Tileset grids are plain images: the tilemap addresses them by tile index.
  for (const [key, tileset] of Object.entries(manifest.tilesets)) {
    loader.image(key, ASSETS_BASE_URL + tileset.image);
  }
}

/**
 * Load lazy atlases (`lazy: true`) the first time they are needed; resolves
 * with those that made it. A failed download resolves too, without it: the
 * caller draws its fallback.
 */
export function loadLazyAtlases(
  scene: Phaser.Scene,
  manifest: Pick<AssetManifest, 'atlases'>,
  keys: readonly string[],
): Promise<Set<string>> {
  const missing = keys.filter(
    (key) => manifest.atlases[key] !== undefined && !scene.textures.exists(key),
  );
  const loaded = (): Set<string> => new Set(keys.filter((key) => scene.textures.exists(key)));
  if (missing.length === 0) return Promise.resolve(loaded());
  return new Promise((resolve) => {
    for (const key of missing) {
      const atlas = manifest.atlases[key];
      if (atlas !== undefined)
        scene.load.atlas(key, ASSETS_BASE_URL + atlas.image, ASSETS_BASE_URL + atlas.data);
    }
    scene.load.once('complete', () => {
      resolve(loaded());
    });
    scene.load.start();
  });
}
